import React, { useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../api';

let L;
try { L = require('leaflet'); } catch(e) {}

// Distinct colors for travel paths — one per tracker
const PATH_COLORS = [
  '#2563eb', // blue
  '#16a34a', // green
  '#9333ea', // purple
  '#dc2626', // red
  '#ea580c', // orange
  '#0891b2', // cyan
  '#d97706', // amber
  '#be185d', // pink
  '#059669', // emerald
  '#7c3aed', // violet
];

export default function MapView({ trackers, selectedTrackerIds, onToggleTracker, subAccountId }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const labelsRef = useRef({});
  const pathLayersRef = useRef({});
  const pathRefreshIntervalRef = useRef(null);

  const onToggleTrackerRef = useRef(onToggleTracker);
  useEffect(() => { onToggleTrackerRef.current = onToggleTracker; }, [onToggleTracker]);

  // Initialize map once
  useEffect(() => {
    if (!L || mapInstanceRef.current) return;
    mapInstanceRef.current = L.map(mapRef.current, {
      center: [29.4241, -98.4936],
      zoom: 11,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
    }).addTo(mapInstanceRef.current);

    setTimeout(() => {
      if (mapInstanceRef.current) mapInstanceRef.current.invalidateSize();
    }, 300);

    const handleResize = () => {
      if (mapInstanceRef.current) mapInstanceRef.current.invalidateSize();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch and draw paths for all selected trackers
  const drawPaths = useCallback(async () => {
    if (!L || !mapInstanceRef.current || !subAccountId) return;

    // Remove paths for deselected trackers
    Object.keys(pathLayersRef.current).forEach(id => {
      if (!selectedTrackerIds.includes(id)) {
        pathLayersRef.current[id].remove();
        delete pathLayersRef.current[id];
      }
    });

    // Draw/refresh paths for selected trackers
    await Promise.all(selectedTrackerIds.map(async (trackerId, index) => {
      try {
        const pings = await apiFetch(
          `/api/trackers/${trackerId}/pings?sub_account_id=${subAccountId}`
        );
        if (!pings || pings.length < 2) return;

        const points = pings.map(p => [parseFloat(p.lat), parseFloat(p.lng)]);
        const color = PATH_COLORS[index % PATH_COLORS.length];

        // Remove existing path before redrawing
        if (pathLayersRef.current[trackerId]) {
          pathLayersRef.current[trackerId].remove();
        }

        pathLayersRef.current[trackerId] = L.polyline(points, {
          color,
          weight: 5,
          opacity: 0.85,
          lineJoin: 'round',
          lineCap: 'round',
        }).addTo(mapInstanceRef.current);
      } catch (err) {
        console.error(`Failed to load travel path for ${trackerId}:`, err.message);
      }
    }));
  }, [selectedTrackerIds, subAccountId]);

  // Draw paths when selection changes
  useEffect(() => {
    drawPaths();
  }, [drawPaths]);

  // Refresh paths every 60 seconds for moving trackers
  useEffect(() => {
    if (pathRefreshIntervalRef.current) {
      clearInterval(pathRefreshIntervalRef.current);
    }

    if (selectedTrackerIds.length === 0) return;

    pathRefreshIntervalRef.current = setInterval(() => {
      const hasMovingTracker = trackers.some(
        t => selectedTrackerIds.includes(t.id) && t.status === 'moving'
      );
      if (hasMovingTracker) {
        drawPaths();
      }
    }, 60000);

    return () => {
      if (pathRefreshIntervalRef.current) {
        clearInterval(pathRefreshIntervalRef.current);
      }
    };
  }, [selectedTrackerIds, trackers, drawPaths]);

  // Update markers in place
  useEffect(() => {
    if (!L || !mapInstanceRef.current) return;

    const map = mapInstanceRef.current;
    const currentIds = new Set(trackers.map(t => t.id));

    // Remove markers for trackers that no longer exist
    Object.keys(markersRef.current).forEach(id => {
      if (!currentIds.has(id)) {
        markersRef.current[id].remove();
        delete markersRef.current[id];
        if (labelsRef.current[id]) {
          labelsRef.current[id].remove();
          delete labelsRef.current[id];
        }
      }
    });

    trackers.forEach((tracker) => {
      if (!tracker.current_lat || !tracker.current_lng) return;

      const isSelected = selectedTrackerIds.includes(tracker.id);
      const pathColor = isSelected
        ? PATH_COLORS[selectedTrackerIds.indexOf(tracker.id) % PATH_COLORS.length]
        : null;
      const dotColor = tracker.status === 'moving' ? '#22c55e' : '#94a3b8';
      const size = isSelected ? 16 : 12;
      const latlng = [tracker.current_lat, tracker.current_lng];

      const icon = L.divIcon({
        html: `<div style="
          width:${size}px; height:${size}px;
          background:${dotColor};
          border:${isSelected ? '3px' : '2px'} solid ${isSelected ? (pathColor || 'white') : 'white'};
          border-radius:50%;
          box-shadow:0 2px 6px rgba(0,0,0,0.4);
        "></div>`,
        className: '',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });

      const popupContent = `
        <div style="font-family:sans-serif;min-width:160px">
          <strong style="font-size:13px">${tracker.name}</strong><br/>
          <span style="color:#64748b;font-size:12px">
            ${tracker.status === 'moving' ? '🟢 Moving' : '⚪ Idle'}<br/>
            ${tracker.total_miles ? Math.round(tracker.total_miles).toLocaleString() + ' mi total' : 'No mileage data'}
          </span>
        </div>
      `;

      if (markersRef.current[tracker.id]) {
        const marker = markersRef.current[tracker.id];
        marker.setLatLng(latlng);
        marker.setIcon(icon);
        marker.setPopupContent(popupContent);
      } else {
        const marker = L.marker(latlng, { icon })
          .addTo(map)
          .bindPopup(popupContent)
          .on('click', () => onToggleTrackerRef.current(tracker.id));
        markersRef.current[tracker.id] = marker;
      }

      // Label for selected trackers
      if (isSelected) {
        if (!labelsRef.current[tracker.id]) {
          labelsRef.current[tracker.id] = L.tooltip({
            permanent: true,
            direction: 'top',
            offset: [0, -10],
            className: 'tracker-label',
          })
            .setContent(tracker.name.split(' - ')[0])
            .setLatLng(latlng);
          labelsRef.current[tracker.id].addTo(map);
        } else {
          labelsRef.current[tracker.id].setLatLng(latlng);
        }
      } else {
        if (labelsRef.current[tracker.id]) {
          labelsRef.current[tracker.id].remove();
          delete labelsRef.current[tracker.id];
        }
      }
    });

    // Fit map to show all selected trackers
    const selectedTrackers = trackers.filter(t =>
      selectedTrackerIds.includes(t.id) && t.current_lat && t.current_lng
    );
    if (selectedTrackers.length === 1) {
      mapInstanceRef.current.panTo(
        [selectedTrackers[0].current_lat, selectedTrackers[0].current_lng],
        { animate: true, duration: 0.5 }
      );
    } else if (selectedTrackers.length > 1) {
      const bounds = L.latLngBounds(
        selectedTrackers.map(t => [t.current_lat, t.current_lng])
      );
      mapInstanceRef.current.fitBounds(bounds, { padding: [40, 40], animate: true });
    }

  }, [trackers, selectedTrackerIds]);

  return (
    <div className="map-container" style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={mapRef} style={{ width: '100%', height: '100%', borderRadius: '8px' }} />
      {selectedTrackerIds.length > 0 && (
        <div className="map-info-overlay" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
          {trackers.filter(t => selectedTrackerIds.includes(t.id)).map((t) => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0,
                background: PATH_COLORS[selectedTrackerIds.indexOf(t.id) % PATH_COLORS.length],
              }} />
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>{t.name}</span>
              <span style={{ fontSize: '11px', color: '#64748b' }}>
                {t.total_miles ? Math.round(t.total_miles).toLocaleString() + ' mi' : '—'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
